/**
 * Standalone judge-response signature verification.
 *
 * `Atbash.judgeAction` already verifies the response signature inline when
 * `verifyPubKey` is set. This wrapper exposes the same check for use
 * outside of `judgeAction` — webhook receivers, stored response replay,
 * test fixtures — preserving the legacy SDK's `{ ok, reason }` contract.
 *
 * Validation (hex format, length bounds, secp256k1 verify) happens in
 * the Rust core via `native.verifySignature`, so all language bindings
 * agree byte-for-byte on what counts as a valid signature.
 */
import { native } from "./native.js";

export function verifyJudgeResponseSignature(
  bodyBytes: Uint8Array,
  signatureHex: string | null,
  pubKeyHex: string,
): { ok: boolean; reason?: string } {
  if (!signatureHex) {
    return { ok: false, reason: "missing X-Atbash-Signature header" };
  }

  const body = Buffer.isBuffer(bodyBytes) ? bodyBytes : Buffer.from(bodyBytes);

  let isValid: boolean;
  try {
    isValid = native.verifySignature(body, signatureHex, pubKeyHex);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err ?? "");
    // Rust core throws on malformed inputs (non-hex, wrong length, etc.).
    // Surface that distinct from "well-formed but doesn't verify" so callers
    // can log differently if they want.
    if (
      message.includes("signature is not hex") ||
      message.includes("signature length out of range")
    ) {
      return { ok: false, reason: "malformed signature header" };
    }
    return { ok: false, reason: `signature verification threw: ${message}` };
  }

  return isValid
    ? { ok: true }
    : {
        ok: false,
        reason: "signature does not verify against configured verifyPubKey",
      };
}
